import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { t, font, Panel, Button, Input, Badge, EmptyState } from "@realtyflows/ui";
import {
  // Webhooks + API keys (V4.9 + 4.10)
  getEventCatalog, listWebhooks, createWebhook, deleteWebhook,
  listApiKeys, createApiKey, revokeApiKey,
  // Lead capture (V4.6 + 4.7)
  getWidgetToken, rotateWidgetToken, getIdxSecrets, createIdxSecret,
  // Calendar (V4.8)
  getCalendarStatus, enableCalendarSync, disableCalendarSync, resyncCalendar,
} from "../api";

/**
 * IntegrationsHub — V4 surfaces UI, consolidated into one page.
 *
 * Tabs:
 *   - Lead Capture: embed widget snippet + IDX webhook URLs
 *   - Calendar: enable/disable Google Calendar sync
 *   - Webhooks: subscribe to events
 *   - API Keys: create/revoke programmatic access tokens
 */
export default function IntegrationsHub() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("lead_capture");

  return (
    <div style={{ animation: "fadeSlide 0.3s ease", maxWidth: 1000, margin: "0 auto" }}>
      <div style={{ marginBottom: 16 }}>
        <button onClick={() => navigate("/settings")} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, color: t.textMuted, fontFamily: font.body }}>
          ← Back to Settings
        </button>
      </div>

      <Panel padding={24} style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 20, fontWeight: 800, color: t.textPrimary, fontFamily: font.display }}>Integrations</div>
        <div style={{ fontSize: 13, color: t.textMuted, marginTop: 4, fontFamily: font.body }}>
          Connect RealtyFlows to your website, IDX provider, Google Calendar, and any tool that speaks webhooks or REST.
        </div>
      </Panel>

      {/* Tab bar */}
      <div style={{ display: "flex", gap: 0, marginBottom: 16, background: t.pageBg, borderRadius: 10, padding: 3, border: `1px solid ${t.border}` }}>
        {[
          { id: "lead_capture", label: "Lead Capture" },
          { id: "calendar", label: "Calendar" },
          { id: "webhooks", label: "Webhooks" },
          { id: "api_keys", label: "API Keys" },
        ].map((tb) => (
          <button key={tb.id} onClick={() => setTab(tb.id)}
            style={{
              flex: 1, padding: "10px 16px", textAlign: "center", borderRadius: 8,
              fontSize: 12, fontWeight: 600, fontFamily: font.body,
              cursor: "pointer", transition: "all 0.2s", border: "none",
              background: tab === tb.id ? t.surface : "transparent",
              color: tab === tb.id ? t.textPrimary : t.textMuted,
              boxShadow: tab === tb.id ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
            }}
          >{tb.label}</button>
        ))}
      </div>

      {tab === "lead_capture" && <LeadCaptureTab />}
      {tab === "calendar" && <CalendarTab />}
      {tab === "webhooks" && <WebhooksTab />}
      {tab === "api_keys" && <ApiKeysTab />}
    </div>
  );
}

// ──────────────────────────────────────────────────────────
// Lead Capture tab (V4.6 + 4.7)
// ──────────────────────────────────────────────────────────
function LeadCaptureTab() {
  const [widget, setWidget] = useState(null);
  const [idxSecrets, setIdxSecrets] = useState([]);
  const [rotating, setRotating] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getWidgetToken().then(setWidget).catch(() => setWidget(null));
    getIdxSecrets().then(setIdxSecrets).catch(() => setIdxSecrets([]));
  }, []);

  const handleRotate = async () => {
    if (!window.confirm("Rotating will invalidate the old embed code. Continue?")) return;
    setRotating(true);
    try {
      await rotateWidgetToken();
      const fresh = await getWidgetToken();
      setWidget(fresh);
    } catch (err) {
      window.alert(err.message);
    } finally {
      setRotating(false);
    }
  };

  const handleCreateIdx = async (provider) => {
    setBusy(true);
    try {
      await createIdxSecret(provider);
      const fresh = await getIdxSecrets();
      setIdxSecrets(fresh);
    } catch (err) {
      window.alert(err.message);
    } finally {
      setBusy(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard?.writeText(text);
  };

  const PROVIDERS = [
    { id: "real_geeks", label: "Real Geeks" },
    { id: "boomtown", label: "BoomTown" },
    { id: "kvcore", label: "kvCORE" },
    { id: "sierra", label: "Sierra Interactive" },
    { id: "generic", label: "Generic webhook" },
  ];
  const existingProviders = new Set(idxSecrets.map((s) => s.provider));

  return (
    <>
      <Panel padding={20} style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, marginBottom: 6, fontFamily: font.display }}>
          Website Lead Widget
        </div>
        <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 14, fontFamily: font.body }}>
          Drop this snippet on your website. Leads land in your CRM with source="Website".
        </div>
        {widget && (
          <>
            <CodeBox value={widget.embed_snippet} onCopy={() => copyToClipboard(widget.embed_snippet)} />
            <div style={{ marginTop: 10, fontSize: 11, color: t.textMuted, fontFamily: font.mono }}>
              Total submissions: <strong>{widget.submission_count}</strong>
              {widget.last_used_at && ` · last received ${new Date(widget.last_used_at).toLocaleString()}`}
            </div>
            <div style={{ marginTop: 12 }}>
              <Button variant="outline" size="sm" onClick={handleRotate} disabled={rotating}>
                {rotating ? "Rotating..." : "Rotate token"}
              </Button>
            </div>
          </>
        )}
      </Panel>

      <Panel padding={20}>
        <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, marginBottom: 6, fontFamily: font.display }}>
          IDX Webhooks
        </div>
        <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 14, fontFamily: font.body }}>
          Configure your IDX provider to POST new leads to a RealtyFlows webhook URL. Leads come in pre-normalized regardless of provider.
        </div>

        {idxSecrets.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
            {idxSecrets.map((s) => (
              <div key={s.id} style={{
                padding: 12, background: t.surfaceWarm,
                border: `1px solid ${t.border}`, borderRadius: 8,
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <Badge color={t.emerald}>{(PROVIDERS.find((p) => p.id === s.provider) || {}).label || s.provider}</Badge>
                  <span style={{ fontSize: 10, color: t.textMuted, fontFamily: font.mono }}>
                    {s.received_count || 0} received
                  </span>
                </div>
                <CodeBox value={s.webhook_url} onCopy={() => copyToClipboard(s.webhook_url)} />
              </div>
            ))}
          </div>
        )}

        <div style={{ fontSize: 11, color: t.textSecondary, fontWeight: 600, marginBottom: 6, fontFamily: font.body }}>
          Add a webhook for another provider:
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {PROVIDERS.filter((p) => !existingProviders.has(p.id)).map((p) => (
            <Button key={p.id} variant="outline" size="sm" disabled={busy} onClick={() => handleCreateIdx(p.id)}>
              + {p.label}
            </Button>
          ))}
        </div>
      </Panel>
    </>
  );
}

// ──────────────────────────────────────────────────────────
// Calendar tab (V4.8)
// ──────────────────────────────────────────────────────────
function CalendarTab() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState("");

  useEffect(() => {
    getCalendarStatus().then(setStatus).catch(() => setStatus(null));
  }, []);

  const handleToggle = async () => {
    setBusy(true);
    try {
      if (status?.syncEnabled) {
        await disableCalendarSync();
        setStatus({ ...status, syncEnabled: false });
      } else {
        await enableCalendarSync();
        setStatus({ ...status, syncEnabled: true });
      }
    } catch (err) {
      window.alert(err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleResync = async () => {
    setBusy(true);
    try {
      const r = await resyncCalendar();
      setToast(`Re-synced ${r.synced} of ${r.scanned} upcoming appointments`);
      setTimeout(() => setToast(""), 3000);
    } catch (err) {
      window.alert(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!status) return null;

  return (
    <Panel padding={20}>
      <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, marginBottom: 6, fontFamily: font.display }}>
        Google Calendar Sync
      </div>
      <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 14, fontFamily: font.body, lineHeight: 1.5 }}>
        Mirrors every appointment + folder milestone to a dedicated "RealtyFlows" calendar on your Google account. Two-way: edits in the CRM update the calendar event automatically.
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
        <StatusRow label="Gmail connected" status={status.connected} />
        <StatusRow label="Calendar permission granted" status={status.hasCalendarScope}
          hint={!status.hasCalendarScope && "Re-connect Gmail with the Calendar scope to enable sync."} />
        <StatusRow label="Sync enabled" status={status.syncEnabled} />
      </div>

      <div style={{ display: "flex", gap: 8 }}>
        <Button variant="primary" onClick={handleToggle} disabled={busy || !status.hasCalendarScope}>
          {status.syncEnabled ? "Disable sync" : "Enable sync"}
        </Button>
        {status.syncEnabled && (
          <Button variant="outline" onClick={handleResync} disabled={busy}>
            Re-sync upcoming appointments
          </Button>
        )}
      </div>

      {toast && (
        <div style={{ marginTop: 12, fontSize: 12, color: t.emerald, fontFamily: font.body }}>{toast}</div>
      )}
    </Panel>
  );
}

function StatusRow({ label, status, hint }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ width: 8, height: 8, borderRadius: 99, background: status ? t.emerald : t.coral, flexShrink: 0 }} />
      <span style={{ fontSize: 12, color: t.textPrimary, fontFamily: font.body }}>{label}</span>
      {hint && (
        <span style={{ fontSize: 11, color: t.textMuted, fontStyle: "italic", fontFamily: font.body, marginLeft: 8 }}>
          {hint}
        </span>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────
// Webhooks tab (V4.9)
// ──────────────────────────────────────────────────────────
function WebhooksTab() {
  const [events, setEvents] = useState([]);
  const [subs, setSubs] = useState([]);
  const [url, setUrl] = useState("");
  const [label, setLabel] = useState("");
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [busy, setBusy] = useState(false);
  const [createdSecret, setCreatedSecret] = useState(null);

  useEffect(() => {
    getEventCatalog().then(setEvents).catch(() => setEvents([]));
    listWebhooks().then(setSubs).catch(() => setSubs([]));
  }, []);

  const handleCreate = async () => {
    if (!/^https?:\/\//.test(url)) { window.alert("Enter a valid http(s) URL"); return; }
    setBusy(true);
    try {
      const created = await createWebhook({ url, label, events: selectedEvents });
      setCreatedSecret(created.secret);  // show secret once
      const refreshed = await listWebhooks();
      setSubs(refreshed);
      setUrl(""); setLabel(""); setSelectedEvents([]);
    } catch (err) {
      window.alert(err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this webhook?")) return;
    await deleteWebhook(id);
    setSubs(subs.filter((s) => s.id !== id));
  };

  const toggleEvent = (ev) => {
    setSelectedEvents(selectedEvents.includes(ev)
      ? selectedEvents.filter((e) => e !== ev)
      : [...selectedEvents, ev]);
  };

  return (
    <>
      {createdSecret && (
        <Panel padding={16} style={{ marginBottom: 16, background: t.amber + "10", border: `1px solid ${t.amber}40` }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: t.amber, marginBottom: 6, fontFamily: font.display }}>
            ⚠ Save this signing secret now
          </div>
          <div style={{ fontSize: 11, color: t.textPrimary, marginBottom: 8, fontFamily: font.body }}>
            We'll never show it again. Use it to verify webhook signatures on your receiver.
          </div>
          <CodeBox value={createdSecret} />
          <div style={{ marginTop: 8 }}>
            <Button variant="primary" size="sm" onClick={() => setCreatedSecret(null)}>I've saved it</Button>
          </div>
        </Panel>
      )}

      <Panel padding={20} style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, marginBottom: 14, fontFamily: font.display }}>
          New webhook subscription
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
          <Input placeholder="Webhook URL (https://...)" value={url} onChange={(e) => setUrl(e.target.value)} />
          <Input placeholder="Label (optional)" value={label} onChange={(e) => setLabel(e.target.value)} />
        </div>
        <div style={{ fontSize: 11, color: t.textSecondary, fontWeight: 600, marginBottom: 6, fontFamily: font.body }}>
          Events ({selectedEvents.length === 0 ? "all events" : `${selectedEvents.length} selected`})
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 12 }}>
          {events.map((ev) => (
            <button key={ev} onClick={() => toggleEvent(ev)} style={{
              padding: "3px 9px", borderRadius: 99,
              background: selectedEvents.includes(ev) ? t.emerald : t.surfaceWarm,
              color: selectedEvents.includes(ev) ? "#fff" : t.textSecondary,
              border: `1px solid ${selectedEvents.includes(ev) ? t.emerald : t.border}`,
              fontSize: 10, fontFamily: font.mono, cursor: "pointer",
            }}>
              {ev}
            </button>
          ))}
        </div>
        <Button variant="primary" onClick={handleCreate} disabled={busy || !url}>
          {busy ? "Creating..." : "Subscribe to webhook"}
        </Button>
      </Panel>

      <Panel padding={20}>
        <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, marginBottom: 14, fontFamily: font.display }}>
          Active subscriptions
        </div>
        {subs.length === 0 ? (
          <EmptyState title="No webhooks yet" sub="Create one above to start receiving event notifications." />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {subs.map((sub) => (
              <div key={sub.id} style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: 12, background: t.surfaceWarm,
                border: `1px solid ${t.border}`, borderRadius: 8,
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: t.textPrimary, fontFamily: font.body }}>
                    {sub.label || sub.url}
                  </div>
                  <div style={{ fontSize: 10, color: t.textMuted, fontFamily: font.mono, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {sub.url}
                  </div>
                  <div style={{ fontSize: 10, color: t.textMuted, fontFamily: font.mono, marginTop: 4 }}>
                    {(sub.events || []).length === 0 ? "All events" : `${(sub.events || []).length} events`} ·{" "}
                    {sub.failure_count > 0 && <span style={{ color: t.coral }}>{sub.failure_count} consecutive failures</span>}
                    {sub.last_delivery_at && ` · last fire ${new Date(sub.last_delivery_at).toLocaleString()}`}
                  </div>
                </div>
                <Badge color={sub.active ? t.emerald : t.textMuted}>{sub.active ? "Active" : "Disabled"}</Badge>
                <button onClick={() => handleDelete(sub.id)} style={{
                  background: "transparent", border: "none", cursor: "pointer", color: t.coral, fontSize: 14, fontWeight: 700,
                }}>×</button>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </>
  );
}

// ──────────────────────────────────────────────────────────
// API Keys tab (V4.10)
// ──────────────────────────────────────────────────────────
function ApiKeysTab() {
  const [keys, setKeys] = useState([]);
  const [label, setLabel] = useState("");
  const [createdKey, setCreatedKey] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    listApiKeys().then(setKeys).catch(() => setKeys([]));
  }, []);

  const handleCreate = async () => {
    if (!label.trim()) return;
    setBusy(true);
    try {
      const result = await createApiKey(label.trim());
      setCreatedKey(result.rawKey);
      const fresh = await listApiKeys();
      setKeys(fresh);
      setLabel("");
    } catch (err) {
      window.alert(err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async (id) => {
    if (!window.confirm("Revoke this API key immediately? Any active integration will stop working.")) return;
    await revokeApiKey(id);
    setKeys(keys.map((k) => (k.id === id ? { ...k, active: false } : k)));
  };

  return (
    <>
      {createdKey && (
        <Panel padding={16} style={{ marginBottom: 16, background: t.amber + "10", border: `1px solid ${t.amber}40` }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: t.amber, marginBottom: 6, fontFamily: font.display }}>
            ⚠ Save this API key now
          </div>
          <div style={{ fontSize: 11, color: t.textPrimary, marginBottom: 8, fontFamily: font.body }}>
            We only show the plaintext key once. Save it somewhere secure — you can't retrieve it later.
          </div>
          <CodeBox value={createdKey} />
          <div style={{ marginTop: 8 }}>
            <Button variant="primary" size="sm" onClick={() => setCreatedKey(null)}>I've saved it</Button>
          </div>
        </Panel>
      )}

      <Panel padding={20} style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, marginBottom: 14, fontFamily: font.display }}>
          New API Key
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Input placeholder="Label (e.g. 'Zapier integration')" value={label} onChange={(e) => setLabel(e.target.value)} />
          <Button variant="primary" onClick={handleCreate} disabled={busy || !label.trim()}>
            {busy ? "Creating..." : "Create key"}
          </Button>
        </div>
      </Panel>

      <Panel padding={20}>
        <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, marginBottom: 14, fontFamily: font.display }}>
          Your API Keys
        </div>
        {keys.length === 0 ? (
          <EmptyState title="No API keys yet" sub="Create one above to use the RealtyFlows REST API at /v1/*." />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {keys.map((k) => (
              <div key={k.id} style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: 12, background: t.surfaceWarm,
                border: `1px solid ${t.border}`, borderRadius: 8,
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: t.textPrimary, fontFamily: font.body }}>
                    {k.label}
                  </div>
                  <div style={{ fontSize: 10, color: t.textMuted, fontFamily: font.mono, marginTop: 4 }}>
                    {k.key_prefix}••••••••
                    {" · "}
                    {k.request_count} requests
                    {k.last_used_at && ` · last used ${new Date(k.last_used_at).toLocaleString()}`}
                  </div>
                </div>
                <Badge color={k.active ? t.emerald : t.textMuted}>{k.active ? "Active" : "Revoked"}</Badge>
                {k.active && (
                  <Button variant="outline" size="sm" onClick={() => handleRevoke(k.id)}>Revoke</Button>
                )}
              </div>
            ))}
          </div>
        )}
        <div style={{ marginTop: 14, fontSize: 11, color: t.textMuted, fontFamily: font.body }}>
          Use the key in <code style={{ fontFamily: font.mono, background: t.surfaceWarm, padding: "2px 6px", borderRadius: 3 }}>Authorization: Bearer rf_live_...</code> or <code style={{ fontFamily: font.mono, background: t.surfaceWarm, padding: "2px 6px", borderRadius: 3 }}>X-API-Key</code> headers against the public REST API at <code style={{ fontFamily: font.mono }}>/v1/*</code>.
        </div>
      </Panel>
    </>
  );
}

// ──────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────
function CodeBox({ value, onCopy }) {
  return (
    <div style={{ display: "flex", gap: 8 }}>
      <pre style={{
        flex: 1, margin: 0, padding: 10,
        background: t.surface, border: `1px solid ${t.border}`,
        borderRadius: 6, fontSize: 10, fontFamily: font.mono,
        color: t.textPrimary, overflowX: "auto", whiteSpace: "pre-wrap", wordBreak: "break-all",
      }}>{value}</pre>
      {onCopy && (
        <Button variant="outline" size="sm" onClick={onCopy}>Copy</Button>
      )}
    </div>
  );
}
